"use client";

import { format } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import {
	Calendar,
	ChevronLeft,
	ChevronRight,
	Filter,
	Newspaper,
	Search,
	Tag,
	User,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React, { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import type { ArticleSummary } from "@/lib/shopify/types";
import { useBlogStore } from "@/lib/store/blog-store";

interface BlogsGridProps {
	articles: ArticleSummary[];
	showFeatured?: boolean;
}

const ARTICLES_PER_PAGE = 9;

const container = {
	hidden: { opacity: 0 },
	show: {
		opacity: 1,
		transition: {
			staggerChildren: 0.08,
			delayChildren: 0.15,
		},
	},
};

const slideUp = {
	hidden: { opacity: 0, y: 40 },
	show: {
		opacity: 1,
		y: 0,
		transition: {
			duration: 0.8,
			ease: [0.21, 0.47, 0.32, 0.98] as const,
		},
	},
};

function ArticleCard({ article }: { article: ArticleSummary }) {
	return (
		<Link href={`/blog/${article.handle}`} className="block group h-full">
			<Card className="h-full overflow-hidden transition-all hover:shadow-lg hover:border-primary/50 hover:-translate-y-1 duration-300 flex flex-col p-0 gap-0">
				<div className="relative aspect-video overflow-hidden shrink-0">
					{article.image ? (
						<Image
							src={article.image.url}
							alt={article.image.altText || article.title}
							fill
							className="object-cover transition-transform duration-500 group-hover:scale-105"
							sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
						/>
					) : (
						<div className="h-full bg-secondary flex items-center justify-center">
							<Newspaper className="h-10 w-10 text-muted-foreground" />
						</div>
					)}
					<div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
						<Badge className="bg-background text-foreground hover:bg-background">
							Read More
						</Badge>
					</div>
				</div>
				<CardContent className="p-5 flex flex-col flex-1">
					<div className="flex items-center gap-2 mb-2 flex-wrap">
						{article.blog?.title && (
							<Badge variant="secondary" className="text-xs">
								{article.blog.title}
							</Badge>
						)}
						{article.tags &&
							article.tags.length > 0 &&
							article.tags.slice(0, 2).map((tag) => (
								<Badge key={tag} variant="outline" className="text-xs">
									{tag}
								</Badge>
							))}
					</div>
					<h3 className="font-semibold text-lg group-hover:text-primary transition-colors line-clamp-2 min-h-13">
						{article.title}
					</h3>
					{article.excerpt && (
						<p className="text-sm text-muted-foreground mt-2 line-clamp-2 md:line-clamp-3 min-h-10">
							{article.excerpt}
						</p>
					)}
					<div className="flex items-center gap-3 mt-auto pt-3 text-xs text-muted-foreground">
						{article.author?.name && (
							<div className="flex items-center gap-1">
								<User className="h-3.5 w-3.5" />
								<span>{article.author.name}</span>
							</div>
						)}
						<span className="text-border">•</span>
						<div className="flex items-center gap-1">
							<Calendar className="h-3.5 w-3.5" />
							<span>
								{format(new Date(article.publishedAt), "MMM d, yyyy")}
							</span>
						</div>
					</div>
				</CardContent>
			</Card>
		</Link>
	);
}

function FeaturedArticleCard({ article }: { article: ArticleSummary }) {
	return (
		<Link href={`/blog/${article.handle}`} className="block group">
			<Card className="overflow-hidden transition-all hover:shadow-xl hover:border-primary/30 duration-300 p-0 gap-0">
				<div className="grid md:grid-cols-2 gap-0 md:gap-6">
					<div className="relative aspect-video md:aspect-auto md:min-h-88 overflow-hidden">
						{article.image ? (
							<Image
								src={article.image.url}
								alt={article.image.altText || article.title}
								fill
								className="object-cover transition-transform duration-500 group-hover:scale-105"
								sizes="(max-width: 768px) 100vw, 50vw"
								priority
							/>
						) : (
							<div className="h-full bg-secondary flex items-center justify-center">
								<Newspaper className="h-16 w-16 text-muted-foreground" />
							</div>
						)}
					</div>
					<CardContent className="p-6 md:p-8 flex flex-col justify-center">
						<div className="flex items-center gap-2 flex-wrap mb-3">
							{article.blog?.title && (
								<Badge variant="secondary">{article.blog.title}</Badge>
							)}
							{article.tags &&
								article.tags.length > 0 &&
								article.tags.slice(0, 3).map((tag) => (
									<Badge key={tag} variant="outline" className="text-xs">
										{tag}
									</Badge>
								))}
						</div>
						<h2 className="text-2xl md:text-3xl font-bold group-hover:text-primary transition-colors line-clamp-2">
							{article.title}
						</h2>
						{article.excerpt && (
							<p className="text-muted-foreground mt-3 line-clamp-3">
								{article.excerpt}
							</p>
						)}
						<div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
							{article.author?.name && (
								<div className="flex items-center gap-1.5">
									<User className="h-4 w-4" />
									<span>{article.author.name}</span>
								</div>
							)}
							<div className="flex items-center gap-1.5">
								<Calendar className="h-4 w-4" />
								<span>
									{format(new Date(article.publishedAt), "MMM d, yyyy")}
								</span>
							</div>
						</div>
						<Button className="mt-6 w-fit" variant="secondary">
							Read Article
						</Button>
					</CardContent>
				</div>
			</Card>
		</Link>
	);
}

type SortOption = "newest" | "oldest" | "a-z" | "z-a";

function BlogControls({
	tags,
	selectedTag,
	onSelectTag,
	searchQuery,
	onSearchChange,
	sortOption,
	onSortChange,
}: {
	tags: string[];
	selectedTag: string | null;
	onSelectTag: (tag: string | null) => void;
	searchQuery: string;
	onSearchChange: (val: string) => void;
	sortOption: SortOption;
	onSortChange: (val: SortOption) => void;
}) {
	const [isOpen, setIsOpen] = useState(false);

	return (
		<div className="mb-8 space-y-4">
			<div className="flex flex-col md:flex-row gap-4 mb-4">
				<div className="relative grow">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
					<Input
						type="search"
						placeholder="Search articles..."
						className="pl-10 h-10 w-full bg-background"
						value={searchQuery}
						onChange={(e) => onSearchChange(e.target.value)}
					/>
				</div>

				<div className="flex gap-2 shrink-0">
					<Button
						variant={isOpen ? "secondary" : "outline"}
						className="gap-2 h-10 px-4"
						onClick={() => setIsOpen(!isOpen)}
					>
						<Filter className="h-4 w-4" />
						<span className="hidden sm:inline">
							{isOpen ? "Hide Filters" : "Filter by Tag"}
						</span>
						<span className="sm:hidden">Filters</span>
						{selectedTag && (
							<Badge
								variant="secondary"
								className="ml-1 text-[10px] h-5 px-1.5"
							>
								{selectedTag}
							</Badge>
						)}
					</Button>

					<Select
						value={sortOption}
						onValueChange={(val) => onSortChange(val as SortOption)}
					>
						<SelectTrigger className="w-35 h-10">
							<SelectValue placeholder="Sort by" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="newest">Newest First</SelectItem>
							<SelectItem value="oldest">Oldest First</SelectItem>
							<SelectItem value="a-z">Title: A-Z</SelectItem>
							<SelectItem value="z-a">Title: Z-A</SelectItem>
						</SelectContent>
					</Select>
				</div>
			</div>

			<Collapsible open={isOpen} onOpenChange={setIsOpen}>
				<CollapsibleContent>
					<motion.div
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: "auto" }}
						exit={{ opacity: 0, height: 0 }}
						className="p-4 border rounded-lg bg-secondary/10 space-y-3"
					>
						<div className="flex items-center justify-between">
							<span className="text-sm font-medium">Filter by Tag</span>
							{selectedTag && (
								<Button
									variant="ghost"
									size="sm"
									className="h-auto p-0 text-muted-foreground hover:text-foreground"
									onClick={() => onSelectTag(null)}
								>
									Clear Filter
								</Button>
							)}
						</div>
						<div className="flex flex-wrap gap-2">
							<Button
								variant={selectedTag === null ? "default" : "outline"}
								size="sm"
								className="rounded-full text-xs h-8 px-3"
								onClick={() => {
									onSelectTag(null);
								}}
							>
								All
							</Button>
							{tags.map((tag) => (
								<Button
									key={tag}
									variant={selectedTag === tag ? "default" : "outline"}
									size="sm"
									className="rounded-full text-xs h-8 px-3"
									onClick={() => {
										onSelectTag(tag);
									}}
								>
									{tag}
								</Button>
							))}
						</div>
					</motion.div>
				</CollapsibleContent>
			</Collapsible>
		</div>
	);
}

function PaginationControls({
	currentPage,
	totalPages,
	onPageChange,
}: {
	currentPage: number;
	totalPages: number;
	onPageChange: (page: number) => void;
}) {
	if (totalPages <= 1) return null;

	let visiblePages: (number | "ellipsis")[] = [];
	if (totalPages <= 5) {
		visiblePages = Array.from({ length: totalPages }, (_, i) => i + 1);
	} else if (currentPage <= 3) {
		visiblePages = [1, 2, 3, 4, "ellipsis", totalPages];
	} else if (currentPage >= totalPages - 2) {
		visiblePages = [
			1,
			"ellipsis",
			totalPages - 3,
			totalPages - 2,
			totalPages - 1,
			totalPages,
		];
	} else {
		visiblePages = [
			1,
			"ellipsis",
			currentPage - 1,
			currentPage,
			currentPage + 1,
			"ellipsis",
			totalPages,
		];
	}

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.4, delay: 0.2 }}
			className="flex items-center justify-center gap-2 mt-12"
		>
			<Button
				variant="outline"
				size="icon"
				className="h-9 w-9"
				onClick={() => onPageChange(currentPage - 1)}
				disabled={currentPage === 1}
				aria-label="Previous page"
			>
				<ChevronLeft className="h-4 w-4" />
			</Button>

			{visiblePages.map((page, index) =>
				page === "ellipsis" ? (
					<span
						// biome-ignore lint/suspicious/noArrayIndexKey: Pagination display is safe
						key={`ellipsis-${index}`}
						className="px-2 text-muted-foreground"
					>
						…
					</span>
				) : (
					<Button
						key={page}
						variant={currentPage === page ? "default" : "outline"}
						size="icon"
						className="h-9 w-9"
						onClick={() => onPageChange(page)}
						aria-label={`Page ${page}`}
						aria-current={currentPage === page ? "page" : undefined}
					>
						{page}
					</Button>
				),
			)}

			<Button
				variant="outline"
				size="icon"
				className="h-9 w-9"
				onClick={() => onPageChange(currentPage + 1)}
				disabled={currentPage === totalPages}
				aria-label="Next page"
			>
				<ChevronRight className="h-4 w-4" />
			</Button>
		</motion.div>
	);
}

export function BlogsGrid({ articles, showFeatured = true }: BlogsGridProps) {
	const { selectedTag, setSelectedTag, currentPage, setCurrentPage } =
		useBlogStore();
	const [searchQuery, setSearchQuery] = useState("");
	const [sortOption, setSortOption] = useState<SortOption>("newest");

	const allTags = useMemo(() => {
		const tagSet = new Set<string>();
		articles.forEach((a) => {
			a.tags?.forEach((t) => {
				tagSet.add(t);
			});
		});
		return Array.from(tagSet).sort();
	}, [articles]);

	const filteredArticles = useMemo(() => {
		let result = [...articles];

		if (selectedTag) {
			result = result.filter((a) => a.tags?.includes(selectedTag));
		}

		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase().trim();
			result = result.filter(
				(a) =>
					a.title.toLowerCase().includes(query) ||
					a.excerpt?.toLowerCase().includes(query) ||
					a.author?.name?.toLowerCase().includes(query),
			);
		}

		result.sort((a, b) => {
			switch (sortOption) {
				case "newest":
					return (
						new Date(b.publishedAt).getTime() -
						new Date(a.publishedAt).getTime()
					);
				case "oldest":
					return (
						new Date(a.publishedAt).getTime() -
						new Date(b.publishedAt).getTime()
					);
				case "a-z":
					return a.title.localeCompare(b.title);
				case "z-a":
					return b.title.localeCompare(a.title);
				default:
					return 0;
			}
		});

		return result;
	}, [articles, selectedTag, searchQuery, sortOption]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Reset page when filters change
	React.useEffect(() => {
		setCurrentPage(1);
	}, [selectedTag, searchQuery, sortOption, setCurrentPage]);

	const isFeaturedMode =
		showFeatured && !selectedTag && !searchQuery && sortOption === "newest";

	const featured =
		isFeaturedMode && filteredArticles.length > 0 ? filteredArticles[0] : null;

	const articlesToPaginate = isFeaturedMode
		? filteredArticles.slice(1)
		: filteredArticles;

	const pageCount =
		Math.ceil(articlesToPaginate.length / ARTICLES_PER_PAGE) || 1;
	const validPage = Math.min(Math.max(1, currentPage), Math.max(1, pageCount));

	const gridStart = (validPage - 1) * ARTICLES_PER_PAGE;
	const gridEnd = gridStart + ARTICLES_PER_PAGE;
	const currentGridArticles = articlesToPaginate.slice(gridStart, gridEnd);

	React.useEffect(() => {
		if (currentPage > 1) {
			window.scrollTo({ top: 0, behavior: "smooth" });
		}
	}, [currentPage]);

	if (articles.length === 0) {
		return (
			<motion.div
				initial={{ opacity: 0, scale: 0.95 }}
				animate={{ opacity: 1, scale: 1 }}
				transition={{ duration: 0.5 }}
				className="text-center py-24"
			>
				<div className="flex justify-center mb-6">
					<div className="bg-secondary p-6 rounded-full">
						<Newspaper className="h-12 w-12 text-primary" />
					</div>
				</div>
				<h2 className="text-2xl font-bold mb-2">No articles yet</h2>
				<p className="text-muted-foreground">
					Check back soon for news and updates from Ice Age Meals.
				</p>
			</motion.div>
		);
	}

	return (
		<div className="space-y-6">
			<BlogControls
				tags={allTags}
				selectedTag={selectedTag}
				onSelectTag={(tag) => {
					setSelectedTag(tag);
					setCurrentPage(1);
				}}
				searchQuery={searchQuery}
				onSearchChange={(val) => {
					setSearchQuery(val);
					setCurrentPage(1);
				}}
				sortOption={sortOption}
				onSortChange={setSortOption}
			/>

			{(selectedTag || searchQuery) && (
				<motion.p
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					className="text-sm text-muted-foreground"
				>
					Showing {filteredArticles.length} article
					{filteredArticles.length !== 1 ? "s" : ""}
					{selectedTag && <> tagged with &ldquo;{selectedTag}&rdquo;</>}
					{selectedTag && searchQuery && " and"}
					{searchQuery && <> matching &ldquo;{searchQuery}&rdquo;</>}
				</motion.p>
			)}

			{filteredArticles.length === 0 && (selectedTag || searchQuery) && (
				<motion.div
					initial={{ opacity: 0, scale: 0.95 }}
					animate={{ opacity: 1, scale: 1 }}
					className="text-center py-16"
				>
					<div className="flex justify-center mb-4">
						<div className="bg-secondary p-5 rounded-full">
							<Tag className="h-10 w-10 text-muted-foreground" />
						</div>
					</div>
					<h3 className="text-xl font-bold mb-2">No articles found</h3>
					<p className="text-muted-foreground mb-4">
						Try adjusting your search or filters.
					</p>
					<div className="flex justify-center gap-2">
						<Button
							variant="outline"
							onClick={() => {
								setSelectedTag(null);
								setSearchQuery("");
							}}
						>
							Clear all filters
						</Button>
					</div>
				</motion.div>
			)}

			<AnimatePresence mode="wait">
				<motion.div
					key={`${selectedTag}-${searchQuery}-${validPage}`}
					variants={container}
					initial="hidden"
					animate="show"
					className="space-y-10"
				>
					{featured && validPage === 1 && (
						<motion.div variants={slideUp}>
							<FeaturedArticleCard article={featured} />
						</motion.div>
					)}

					{currentGridArticles.length > 0 && (
						<motion.div
							variants={container}
							className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8"
						>
							{currentGridArticles.map((article) => (
								<motion.div key={article.id} variants={slideUp}>
									<ArticleCard article={article} />
								</motion.div>
							))}
						</motion.div>
					)}
				</motion.div>
			</AnimatePresence>

			<PaginationControls
				currentPage={validPage}
				totalPages={pageCount}
				onPageChange={setCurrentPage}
			/>
		</div>
	);
}
